import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { scheduleListingReopen, updateListingMetadata } from '../../util/api';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 42;

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';

export const SET_REOPEN_DATE_REQUEST = 'app/ManageListingsPage/SET_REOPEN_DATE_REQUEST';
export const SET_REOPEN_DATE_SUCCESS = 'app/ManageListingsPage/SET_REOPEN_DATE_SUCCESS';
export const SET_REOPEN_DATE_ERROR = 'app/ManageListingsPage/SET_REOPEN_DATE_ERROR';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  settingReopenDate: null,
  settingReopenDateError: null,
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

/**
 * Patch a listing's `metadata.temporaryClosure` in place. The schedule-reopen
 * endpoint answers with plain JSON rather than a listing resource (the Integration
 * SDK's UUID/Date instances do not survive JSON), so the card is refreshed from the
 * value we just wrote instead of from a response entity.
 */
const updateTemporaryClosure = (state, listingId, temporaryClosure) => {
  const oldListing = state.ownEntities.ownListing?.[listingId];
  if (!oldListing) {
    return state;
  }
  const updatedListing = {
    ...oldListing,
    attributes: {
      ...oldListing.attributes,
      metadata: { ...oldListing.attributes.metadata, temporaryClosure },
    },
  };
  return {
    ...state,
    ownEntities: {
      ...state.ownEntities,
      ownListing: { ...state.ownEntities.ownListing, [listingId]: updatedListing },
    },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case SET_REOPEN_DATE_REQUEST:
      return {
        ...state,
        settingReopenDate: payload.listingId,
        settingReopenDateError: null,
      };
    case SET_REOPEN_DATE_SUCCESS:
      return {
        ...updateTemporaryClosure(state, payload.listingId, payload.temporaryClosure),
        settingReopenDate: null,
      };
    case SET_REOPEN_DATE_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        settingReopenDate: null,
        settingReopenDateError: {
          listingId: state.settingReopenDate,
          error: payload,
        },
      };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const setReopenDateRequest = listingId => ({
  type: SET_REOPEN_DATE_REQUEST,
  payload: { listingId },
});

// `listingId` here is the plain uuid string: it keys the entity cache directly.
export const setReopenDateSuccess = (listingId, temporaryClosure) => ({
  type: SET_REOPEN_DATE_SUCCESS,
  payload: { listingId, temporaryClosure },
});

export const setReopenDateError = e => ({
  type: SET_REOPEN_DATE_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// Throwing error for new (loadData may need that info)
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));

  const { perPage, ...rest } = queryParams;
  const params = { ...rest, perPage };

  return sdk.ownListings
    .query(params)
    .then(response => {
      dispatch(addOwnEntities(response));
      dispatch(queryListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

/** Callers pass a UUID instance; the JSON endpoints want the plain string. */
const uuidOf = listingId => listingId?.uuid || listingId;

/**
 * Close a listing, optionally scheduling its automatic reopening.
 *
 * `closeData` carries the close reason plus `reopenAt` ('YYYY-MM-DD', or null for an
 * indefinite closure). The reopen date is written BEFORE the listing is closed, and
 * a failure there aborts the close: closing first would leave a host believing they
 * had paused their pool for three months when in fact it is shut for good — the very
 * situation this feature exists to prevent. Scheduling a reopen on a listing that is
 * still open is harmless, since the cron only ever touches closed listings.
 *
 * The close reason stays fire-and-forget: it is analytics, not a promise to the host.
 */
export const closeListing = (listingId, closeData) => async (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  const { reopenAt = null, presetKey } = closeData || {};

  if (reopenAt) {
    try {
      await scheduleListingReopen({ listingId: uuidOf(listingId), reopenAt, presetKey });
    } catch (e) {
      dispatch(closeListingError(storableError(e)));
      return null;
    }
  }

  try {
    const response = await sdk.ownListings.close({ id: listingId }, { expand: true });
    dispatch(closeListingSuccess(response));

    if (closeData) {
      updateListingMetadata({
        listingId,
        metadata: { closeReason: closeData, closedAt: new Date().toISOString() },
      }).catch(e => {
        console.error('Failed to save close reason:', e);
      });
    }
    return response;
  } catch (e) {
    dispatch(closeListingError(storableError(e)));
    return null;
  }
};

export const openListing = listingId => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(openListingSuccess(response));
      // Drop any pending reopen schedule — the host beat the cron to it. Best-effort:
      // a leftover date on an open listing is ignored by the cron anyway.
      scheduleListingReopen({ listingId: uuidOf(listingId), reopenAt: null }).catch(e => {
        console.error('Failed to clear the reopen schedule:', e);
      });
      return response;
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

/**
 * Change (or drop) the reopen date of an already closed listing. Dropping it leaves
 * the listing closed indefinitely; reopening it now is what `openListing` is for.
 */
export const setReopenDate = (listingId, { reopenAt, presetKey }) => async dispatch => {
  dispatch(setReopenDateRequest(listingId));

  try {
    const { temporaryClosure } = await scheduleListingReopen({
      listingId: uuidOf(listingId),
      reopenAt,
      presetKey,
    });
    dispatch(setReopenDateSuccess(uuidOf(listingId), temporaryClosure));
    return temporaryClosure;
  } catch (e) {
    dispatch(setReopenDateError(storableError(e)));
    return null;
  }
};

export const loadData = (params, search, config) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return queryOwnListings({
    ...queryParams,
    page,
    perPage: RESULT_PAGE_SIZE,
    include: ['images', 'currentStock'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    // 'limit.images': 1,
  });
};
